// GUSA-10847
/* eslint-disable max-lines */
/* eslint-disable max-statements */
import {type SagaIterator} from 'redux-saga';
import {map, filter, isEmpty, or} from 'ramda';
import {FULFILLED} from 'redux-promise-middleware';
import request from 'axios';
import type {ActionType} from 'constants/flow/flowTypes';
import {CATEGORY} from 'constants/actions';
import {CATEGORY_PAGE} from 'constants/analytics/events';

import type {FilterActionType, ActiveFilterType} from 'reducers/category/filters/filtersType';
import {select, put, putResolve, call, takeLatest, all, cancelled} from 'redux-saga/effects';
import {getFilters, getActiveFilters} from 'selectors/filters/filters';
import {sendGTMAnalytics} from 'libs/analytics/analyticsGTM';
import {
    getCategoryId,
    getActiveSort,
    getIsCategoryFulfilled,
    getIsCategoryItemsFulfilled,
} from 'selectors/category/category';
import {
    setActiveFilters,
    getCategoryItem,
    setPageNumber,
    getCategoryItemCount,
} from 'actions/category/category';
import {getActiveFiltersFromUrl} from 'libs/category/category';
import {globalDataLayerPageSaga} from 'actions/globalDataLayer/globalDataLayer';
import {waitForFulfilled} from 'libs/utils/utils';
import {getSearchParams} from 'selectors/router/router';
import {applyMyFitOnCategory} from 'features/myFit/reducers/myFit';
import {getNewUrlForEyeWearFilter} from 'features/experiments/eyeWearFilter/utils';

import {createUrlString, urlParamsWithoutFilters} from './helpers/helpers';

/**
 * Update category items with active filters
 * @param {ActiveFilterType} activeFilters - New active filters
 */
export function* updateCategoryItem(activeFilters: ActiveFilterType): SagaIterator {
    const source = yield call([request.CancelToken, 'source']);

    try {
        const categoryId = yield select(getCategoryId);
        const activeSort = yield select(getActiveSort);

        const options = {
            append: false,
            page: 1,
            dir: activeSort.value,
            order: activeSort.option,
            cancelToken: source.token,
        };

        yield all([
            putResolve(getCategoryItem(categoryId, activeFilters, options)),
            putResolve(getCategoryItemCount(categoryId, activeFilters, options)),
        ]);
    } finally {
        if (yield cancelled()) yield call([source, 'cancel']);
    }
}

/**
 * Send GTM analytics
 * @param {string} codeFilter - Selected code filter
 * @param {string} title - Title of filter item
 */
export function* sendAnalytics(codeFilter: string, title: string): SagaIterator {
    const filters = yield select(getFilters);
    const filterGroupTitle = filters.items
        .find(item => item.code === codeFilter)
        .title.toLowerCase();

    yield call(sendGTMAnalytics, {
        eventCategory: CATEGORY_PAGE.EVENT_CATEGORY,
        eventAction: CATEGORY_PAGE.ACTION.FILTER,
        eventLabel: `${filterGroupTitle} - ${title}`,
    });
}

/**
 * Update filters in URL
 * @param {ActiveFilterType} activeFilters - Active filters
 */
export function* updateUrlFilters(activeFilters: ActiveFilterType): SagaIterator {
    const currentParams = yield call(urlParamsWithoutFilters, activeFilters);

    const params = {...currentParams, ...activeFilters, p: [{value: 1}]};
    if (params.q) params.q = [{value: params.q[0]}];
    if (params.order) params.order = map(value => ({value}), params.order);
    if (params.pairfectMatches) delete params.pairfectMatches;

    // TODO: Replace to react-router-redux
    yield call(
        [window.history, 'replaceState'],
        window.history.state,
        document.title,
        `${window.location.pathname}?${createUrlString(params)}`,
    );
}

/**
 * Updating active filters
 */
export function* updateActiveFiltersSaga({
    payload: {codeFilter, value, item: selectedItem},
}: ActionType<FilterActionType>): SagaIterator {
    const activeFilters = yield select(getActiveFilters);
    activeFilters[codeFilter] = or(activeFilters[codeFilter], []);

    const index = activeFilters[codeFilter].findIndex(item => item.value === value);
    if (index >= 0) {
        activeFilters[codeFilter].splice(index, 1);
    } else {
        activeFilters[codeFilter].push(selectedItem);
        yield call(sendAnalytics, codeFilter, selectedItem.title);
    }

    yield all([
        put(setActiveFilters(filter(item => !isEmpty(item), activeFilters))),
        put(setPageNumber(1)),
        call(updateCategoryItem, activeFilters),
        call(updateUrlFilters, activeFilters),
    ]);
}

/**
 * Analytic after category items fetching
 */
export function* sendAnalyticAfterCategoryItems(): SagaIterator {
    yield put(globalDataLayerPageSaga('category'));
}

/**
 * Set active filters from url
 * @param {{isEyeWearExp: boolean, customUrl: string }} eyeWearsParams props with eyeWearExp params
 */
export function* setActiveFiltersFromUrl(eyeWearsParams): SagaIterator {
    yield call(waitForFulfilled, getIsCategoryFulfilled, `${CATEGORY.GET_CATEGORY}_${FULFILLED}`);
    const [{items: filterItems}, url] = yield all([select(getFilters), select(getSearchParams)]);
    const [filterList, id, activeSort] = yield all([
        call(
            getActiveFiltersFromUrl,
            filterItems,
            (eyeWearsParams?.isEyeWearExp && eyeWearsParams.customUrl) || url,
        ),
        select(getCategoryId),
        select(getActiveSort),
    ]);

    const additionalData = {
        append: false,
        page: 1,
        order: activeSort.option || '',
        dir: activeSort.value || '',
    };

    yield put(setActiveFilters(filterList));
    if (__NODEJS__) {
        yield putResolve(getCategoryItem(id, filterList, additionalData));
    } else {
        yield all([
            putResolve(getCategoryItemCount(id, filterList, {append: false, page: 1})),
            putResolve(getCategoryItem(id, filterList, additionalData)),
        ]);
    }
    yield putResolve(applyMyFitOnCategory());

    if (!__NODEJS__) yield call(sendAnalyticAfterCategoryItems);
}

/**
 * Setting eyewear filter for experiment
 * @param {action} action props
 */
export function* setEyeWearExpFilter(action) {
    const isCategoryFulfilled = yield select(getIsCategoryItemsFulfilled);
    if (isCategoryFulfilled) {
        const {items: filterItems} = yield select(getFilters);
        const customUrl = yield call(getNewUrlForEyeWearFilter, action, filterItems);

        yield call(
            [window.history, 'replaceState'],
            window.history.state,
            document.title,
            `${window.location.pathname}${customUrl}`,
        );

        yield call(setActiveFiltersFromUrl, {isEyeWearExp: true, customUrl});
    }
}

/**
 * Root filters saga
 */
export default function* rootFilterSaga(): SagaIterator {
    yield takeLatest(CATEGORY.CHANGED_FILTER, updateActiveFiltersSaga);
    yield takeLatest(`${CATEGORY.GET_FILTERS_CATEGORY}_${FULFILLED}`, setActiveFiltersFromUrl);
    yield takeLatest(CATEGORY.SET_EYE_WEAR_FILTER, setEyeWearExpFilter);
}
